import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Headphones, Circle, User, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import ChatView from "@/components/ChatView";
import { useLang } from "@/contexts/LangContext";
import { formatLastSeen, isOnline } from "@/lib/presence";

export default function Chat() {
    const { t } = useLang();
    const [conversations, setConversations] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const activeId = searchParams.get("c");

    const load = () => api.get("/chat/conversations").then((r) => setConversations(r.data));
    useEffect(() => {
        load();
        const iv = setInterval(load, 8000);
        return () => clearInterval(iv);
    }, []);

    const open = (id) => setSearchParams({ c: id });

    return (
        <Layout>
            <div className="max-w-6xl mx-auto px-2 md:px-8 py-6 md:py-10">
                <h1 className="text-3xl md:text-4xl font-black text-espresso mb-6 px-2">{t("chats")}</h1>
                <div className="bg-white border border-border rounded-3xl overflow-hidden grid grid-cols-1 md:grid-cols-3 min-h-[70vh]">
                    {/* Sidebar */}
                    <aside className={`md:col-span-1 border-e border-border ${activeId ? "hidden md:block" : ""}`} data-testid="convs-sidebar">
                        <div className="p-4 border-b border-border font-bold text-espresso">{t("chats")}</div>
                        <div className="overflow-y-auto max-h-[65vh]">
                            {conversations.length === 0 ? (
                                <div className="text-center text-muted-foreground text-sm py-16">
                                    <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-40" />
                                    {t("noConversations")}
                                </div>
                            ) : (
                                conversations.map((c) => {
                                    const active = c.conversation_id === activeId;
                                    const isSup = c.kind === "support";
                                    const online = isOnline(c.peer_last_seen);
                                    return (
                                        <button
                                            key={c.conversation_id}
                                            onClick={() => open(c.conversation_id)}
                                            className={`w-full flex items-start gap-3 p-4 hover:bg-surface transition-colors text-start border-s-4 ${active ? "border-terracotta bg-terracotta/5" : "border-transparent"}`}
                                            data-testid={`conv-item-${c.conversation_id}`}
                                        >
                                            <div className="relative w-11 h-11 rounded-full bg-surface grid place-items-center overflow-hidden flex-shrink-0">
                                                {isSup ? <Headphones className="w-5 h-5 text-terracotta" /> : c.peer_picture ? <img src={c.peer_picture} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-muted-foreground" />}
                                                {online && !isSup && <span className="absolute bottom-0 end-0 w-3 h-3 rounded-full bg-green-500 border-2 border-white" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="font-bold text-sm text-espresso truncate">{c.peer_name || (isSup ? t("supportChat") : "...")}</div>
                                                    {c.unread > 0 && <span className="text-[10px] bg-terracotta text-white font-bold rounded-full h-5 min-w-[20px] px-1 grid place-items-center">{c.unread}</span>}
                                                </div>
                                                <div className="text-xs text-muted-foreground truncate mt-0.5">{c.last_message_preview || "—"}</div>
                                            </div>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </aside>

                    {/* Chat view */}
                    <section className={`md:col-span-2 ${activeId ? "" : "hidden md:block"}`}>
                        <ChatView convId={activeId} onBack={() => setSearchParams({})} embedded />
                    </section>
                </div>
            </div>
        </Layout>
    );
}
